import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiUrl, assetUrl } from '@/config/api';
import { useAuth } from '@/context/AuthContext.jsx';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import normalizeLanguageCode, { deckLanguageOptions } from '@/utils/language.js';
import { deckTextColorOptions, getDeckCardTextStyle } from '@/utils/deckTextColors.js';
import CardPreviewModal from '@/components/CardPreviewModal.jsx';
import DeckImageEditor from '@/components/DeckImageEditor.jsx';
import DeckUploadProgress from '@/components/DeckUploadProgress.jsx';

const CARD_COUNT = 72;
const emptyDeckForm = { name: '', backgroundUrl: '', language: 'ua', textColor: '', renderMode: 'TEXT' };

const buildDefaultCards = (templateCards = []) => {
  const templateMap = {};
  templateCards.forEach((card) => {
    const cellNumber = Number(card?.cellNumber);
    if (Number.isInteger(cellNumber) && cellNumber >= 1 && cellNumber <= CARD_COUNT) {
      templateMap[cellNumber] = {
        title: card?.title ?? '',
        text: card?.text ?? '',
      };
    }
  });

  return Array.from({ length: CARD_COUNT }, (_, index) => {
    const cellNumber = index + 1;
    const fromTemplate = templateMap[cellNumber];
    return {
      id: `cell-${cellNumber}`,
      cellNumber,
      title: fromTemplate?.title || '',
      text: fromTemplate?.text || '',
    };
  });
};

const normalizeDeck = (deck) => ({
  ...deck,
  id: Number(deck.id),
  name: deck.name ?? '',
  backgroundUrl: deck.backgroundUrl ?? deck.cardBackgroundUrl ?? '',
  isSystem: Boolean(deck.isSystem ?? deck.system ?? deck.isDefault ?? deck.createdInAdmin),
  language: normalizeLanguageCode(deck.language),
  textColor: deck.textColor || '',
});

const CreateDeckPage = () => {
  const { token, user } = useAuth();
  const { navigate } = useLanguageNavigation();
  const { t } = useTranslation('dashboard');

  const [deckForm, setDeckForm] = useState(emptyDeckForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [cards, setCards] = useState(buildDefaultCards());
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsError, setCardsError] = useState('');
  const [selectedImageFiles, setSelectedImageFiles] = useState({});
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadBatch, setUploadBatch] = useState(null);

  const [cardBacks, setCardBacks] = useState([]);
  const [cardBacksError, setCardBacksError] = useState('');
  const [cardBacksLoading, setCardBacksLoading] = useState(false);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [backPickerOpen, setBackPickerOpen] = useState(false);
  const [backUploadError, setBackUploadError] = useState('');
  const [backUploading, setBackUploading] = useState(false);
  const [selectedBackUrl, setSelectedBackUrl] = useState('');
  const [backDeleteConfirm, setBackDeleteConfirm] = useState(null);
  const [backDeleteError, setBackDeleteError] = useState('');
  const [backDeleting, setBackDeleting] = useState(false);

  const backFileInputRef = useRef(null);

  const cardBackOptions = useMemo(() => cardBacks, [cardBacks]);
  const cardBackUrl = useMemo(() => deckForm.backgroundUrl || '', [deckForm.backgroundUrl]);
  const resolvedCardBackUrl = useMemo(() => assetUrl(cardBackUrl), [cardBackUrl]);

  const firstCard = useMemo(() => {
    if (!cards || cards.length === 0) {
      return {
        cellNumber: 1,
        title: t('deckDetails.cards.placeholders.title'),
        text: t('deckDetails.cards.placeholders.text'),
      };
    }
    const fromFirstCell = cards.find((card) => card.cellNumber === 1);
    if (fromFirstCell) {
      return fromFirstCell;
    }
    return cards[0];
  }, [cards, t]);

  useEffect(() => {
    setSelectedBackUrl(cardBackUrl);
    setBackUploadError('');
  }, [cardBackUrl]);

  useEffect(() => {
    if (!token) {
      return;
    }
    const fetchSystemDeckTemplate = async () => {
      setCardsLoading(true);
      setCardsError('');
      try {
        const response = await fetch(apiUrl('/api/decks'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || t('createDeck.messages.systemDecksLoadError'));
        }
        const data = await response.json();
        const list = Array.isArray(data.decks) ? data.decks : [];
        const normalized = list.map(normalizeDeck);
        const systemDeck = normalized.find((deck) => deck.isSystem && Array.isArray(deck.cards));
        const templateCards = systemDeck?.cards || [];
        setCards(buildDefaultCards(templateCards));
      } catch (cardsFetchError) {
        setCards(buildDefaultCards());
        setCardsError(cardsFetchError.message);
      } finally {
        setCardsLoading(false);
      }
    };

    fetchSystemDeckTemplate();
  }, [token]);

  useEffect(() => {
    if (!token) {
      return;
    }
    const fetchCardBacks = async () => {
      setCardBacksLoading(true);
      setCardBacksError('');
      try {
        const response = await fetch(apiUrl('/api/card-backs'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || t('createDeck.messages.cardBacksLoadError'));
        }
        const data = await response.json();
        const backs = Array.isArray(data.cardBacks) ? data.cardBacks : [];
        setCardBacks(backs);
      } catch (backsError) {
        setCardBacksError(backsError.message);
        setCardBacks([]);
      } finally {
        setCardBacksLoading(false);
      }
    };

    fetchCardBacks();
  }, [token]);

  const handleDeckFormChange = (field, value) => {
    const nextValue = field === 'language' ? normalizeLanguageCode(value) : value;
    setDeckForm((prev) => ({ ...prev, [field]: nextValue }));
    setError('');
  };

  const openPreviewModal = () => {
    if (!cardBackUrl) {
      return;
    }
    setPreviewModalOpen(true);
  };

  const handleBackSelection = (url) => {
    setSelectedBackUrl(url);
    setBackUploadError('');
  };

  const handleBackSave = () => {
    handleDeckFormChange('backgroundUrl', selectedBackUrl || '');
    setBackPickerOpen(false);
  };

  const canDeleteCardBack = (back) =>
    Number.isInteger(back?.id) && !back.isSystem && back.ownerId && back.ownerId === user?.id;

  const requestDeleteCardBack = (back) => {
    setBackDeleteConfirm(back);
    setBackDeleteError('');
  };

  const closeDeleteConfirm = () => {
    setBackDeleteConfirm(null);
    setBackDeleteError('');
  };

  const handleBackDelete = async () => {
    if (!backDeleteConfirm?.id || !token) {
      return;
    }
    setBackDeleting(true);
    setBackDeleteError('');
    try {
      const response = await fetch(apiUrl(`/api/card-backs/${backDeleteConfirm.id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.error || t('deckDetails.messages.cardBackDeleteError');
        throw new Error(message === 'INTERNAL_ERROR' ? t('deckDetails.messages.cardBackDeleteError') : message);
      }
      setCardBacks((prev) => prev.filter((back) => back.id !== backDeleteConfirm.id));
      if (selectedBackUrl === backDeleteConfirm.imageUrl) {
        handleDeckFormChange('backgroundUrl', '');
        setSelectedBackUrl('');
      }
      closeDeleteConfirm();
    } catch (deleteError) {
      setBackDeleteError(deleteError.message);
    } finally {
      setBackDeleting(false);
    }
  };

  const handleBackFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!token) {
      setBackUploadError(t('deckDetails.messages.authRequired'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setBackUploadError(t('deckDetails.messages.fileSize'));
      event.target.value = '';
      return;
    }

    setBackUploading(true);
    setBackUploadError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(apiUrl('/api/upload/card-back'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || t('deckDetails.messages.fileUploadError'));
      }
      if (data.url) {
        const newBack = data.cardBack || {
          id: `custom-${Date.now()}`,
          name: file.name?.split('.')?.[0] || t('deckDetails.cardBack.custom'),
          imageUrl: data.url,
          thumbnailUrl: data.thumbnailUrl || data.url,
          isSystem: false,
          ownerId: user?.id ?? null,
        };
        setCardBacks((prev) => {
          const filtered = prev.filter((back) => back.imageUrl !== newBack.imageUrl);
          const displayName =
            newBack.name ||
            t('deckDetails.cardBack.customWithIndex', { index: filtered.length + 1 });
          return [{ ...newBack, name: displayName }, ...filtered];
        });
        handleDeckFormChange('backgroundUrl', newBack.imageUrl);
        setSelectedBackUrl(newBack.imageUrl);
      }
    } catch (uploadError) {
      setBackUploadError(uploadError.message);
    } finally {
      setBackUploading(false);
      if (backFileInputRef.current) {
        backFileInputRef.current.value = '';
      }
    }
  };

  const handleCardInputChange = (cellNumber, field, value) => {
    setCards((prev) =>
      prev.map((card) => (card.cellNumber === cellNumber ? { ...card, [field]: value } : card)),
    );
  };

  const handleCardImagesSelected = (entries) => {
    setSelectedImageFiles((prev) => {
      const next = { ...prev };
      entries.forEach(([cellNumber, file]) => { next[cellNumber] = file; });
      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!deckForm.name.trim()) {
      setError(t('deckDetails.messages.nameRequired'));
      return;
    }
    try {
      setSaving(true);
      const preparedCards = cards.map((card) => ({
        cellNumber: card.cellNumber,
        title: card.title?.trim() || t('createDeck.cards.autoTitle', { number: card.cellNumber }),
          text: card.text?.trim() || '',
        }));
      const response = await fetch(apiUrl('/api/decks'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: deckForm.name.trim(),
          cardBackgroundUrl: deckForm.backgroundUrl?.trim() || null,
          language: deckForm.language,
          textColor: deckForm.textColor || null,
          renderMode: deckForm.renderMode,
          cards: preparedCards,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t('createDeck.messages.createError'));
      }
      const data = await response.json().catch(() => ({}));
      const deckId = data.deck?.id ?? data.id;
      if (deckId && deckForm.renderMode === 'IMAGE') {
        const pending = Object.entries(selectedImageFiles);
        let completed = 0;
        let failures = 0;
        let nextIndex = 0;
        setUploadProgress(t('imageDeck.progress', { count: 0, total: pending.length }));
        setUploadBatch(pending.length > 1 ? { done: 0, total: pending.length, failed: 0 } : null);
        const uploadNext = async () => {
          while (nextIndex < pending.length) {
            const [cellNumber, file] = pending[nextIndex++];
            const body = new FormData();
            body.append('file', file);
            try {
              const upload = await fetch(apiUrl(`/api/decks/${deckId}/cards/${cellNumber}/image`), {
                method: 'POST', headers: { Authorization: `Bearer ${token}` }, body,
              });
              if (!upload.ok) failures += 1;
            } catch (uploadError) {
              console.error('Failed to upload image card', uploadError);
              failures += 1;
            }
            completed += 1;
            setUploadProgress(t('imageDeck.progress', { count: completed, total: pending.length }));
            setUploadBatch(pending.length > 1 ? { done: completed, total: pending.length, failed: failures } : null);
          }
        };
        await Promise.all(Array.from({ length: Math.min(3, pending.length) }, uploadNext));
        setUploadBatch(null);
        navigate(`/dashboard/my-cards/${deckId}`, {
          state: { imageDeckUploadFailed: failures > 0 },
        });
        return;
      }
      if (deckId) {
        await fetch(apiUrl('/api/decks/current'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ deckId }),
        }).catch(() => {});
        navigate('/dashboard/my-cards', { state: { selectDeckId: Number(deckId) } });
        return;
      }
      navigate('/dashboard/my-cards');
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setUploadBatch(null);
      setSaving(false);
    }
  };

  return (
    <div className="page-card">
      <div className="page-card-header deck-header">
        <div>
          <p className="page-caption">{t('createDeck.heading.caption')}</p>
          <h1>{t('createDeck.heading.title')}</h1>
          <p className="page-description">{t('createDeck.heading.description')}</p>
        </div>
        <div className="deck-header-actions">
          <button
            type="button"
            className="ghost-button"
            onClick={() => navigate('/dashboard/my-cards')}
            disabled={saving}
          >
            {t('deckDetails.buttons.backToList')}
          </button>
          <button type="submit" className="primary-button" form="deck-form" disabled={saving}>
            {saving ? t('createDeck.buttons.creating') : (deckForm.renderMode === 'IMAGE' ? t('imageDeck.saveDraft') : t('createDeck.buttons.create'))}
          </button>
        </div>
      </div>

        <div className="deck-editor-grid">
          <form id="deck-form" className="deck-form-card" onSubmit={handleSubmit}>
            <h2>{t('createDeck.form.title')}</h2>
            <label className="form-field">
              <span>{t('deckDetails.fields.name.label')}</span>
              <input
                type="text"
                value={deckForm.name}
                onChange={(event) => handleDeckFormChange('name', event.target.value)}
                placeholder={t('deckDetails.fields.name.placeholder')}
                required
                disabled={saving}
              />
            </label>
            <label className="form-field">
              <span>{t('deckDetails.fields.language.label')}</span>
              <select
                value={deckForm.language}
                onChange={(event) => handleDeckFormChange('language', event.target.value)}
                disabled={saving}
              >
                {deckLanguageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span>{t('imageDeck.modeLabel')}</span>
              <select value={deckForm.renderMode} onChange={(event) => handleDeckFormChange('renderMode', event.target.value)} disabled={saving}>
                <option value="TEXT">{t('imageDeck.textMode')}</option>
                <option value="IMAGE">{t('imageDeck.imageMode')}</option>
              </select>
            </label>
            {deckForm.renderMode === 'TEXT' && (<>
            <div className="form-field card-back-preview-field">
              <div className="card-back-preview-header">
                <span>{t('deckDetails.fields.cardBack.label')}</span>
                <span className="form-hint">{t('deckDetails.fields.cardBack.hint')}</span>
              </div>
              <div className={`card-back-preview${cardBackUrl ? '' : ' empty'}`}>
                <div
                  className="card-back-preview__image"
                  style={cardBackUrl ? { backgroundImage: `url(${resolvedCardBackUrl})` } : {}}
                >
                  {!cardBackUrl && (
                    <span className="card-back-preview__placeholder">
                      {t('deckDetails.fields.cardBack.placeholder')}
                    </span>
                  )}
                  <div className="card-back-preview__actions">
                    <button
                      type="button"
                      className="icon-button"
                      onClick={openPreviewModal}
                      disabled={!cardBackUrl}
                      title={t('deckDetails.fields.cardBack.preview')}
                    >
                      👁
                    </button>
                    <button
                      type="button"
                      className="icon-button"
                      onClick={() => setBackPickerOpen(true)}
                      title={t('deckDetails.fields.cardBack.edit')}
                    >
                      ✏️
                    </button>
                  </div>
                </div>
              </div>
              {cardBacksError && <p className="form-error">{cardBacksError}</p>}
            </div>
            <label className="form-field">
              <span>{t('deckDetails.fields.textColor.label')}</span>
              <select
                value={deckForm.textColor}
                onChange={(event) => handleDeckFormChange('textColor', event.target.value)}
                disabled={saving}
              >
                {deckTextColorOptions.map((option) => (
                  <option key={option.value || 'default'} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </label>
            </>)}
            {error && <p className="form-error">{error}</p>}
          </form>

          <div className="cards-section">
            <div className="cards-section-header">
              <h2>{t('createDeck.cards.title', { count: CARD_COUNT })}</h2>
              <p>{t('createDeck.cards.description')}</p>
            </div>
            {cardsError && <p className="form-error">{cardsError}</p>}
            {cardsLoading ? (
              <p className="page-description">{t('createDeck.cards.loading')}</p>
            ) : deckForm.renderMode === 'IMAGE' ? (
              <DeckImageEditor
                cards={cards}
                selectedFiles={selectedImageFiles}
                onFilesSelected={handleCardImagesSelected}
                busy={saving}
                progress={uploadProgress}
              />
            ) : (
              <div className="cards-table-wrapper">
                <table className="cards-table">
                  <thead>
                    <tr>
                      <th>{t('deckDetails.cards.headers.cell')}</th>
                      <th>{t('deckDetails.cards.headers.title')}</th>
                      <th>{t('deckDetails.cards.headers.text')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cards.map((card) => (
                      <tr key={card.id}>
                        <td className="cell-number">{card.cellNumber}</td>
                        <td>
                          <input
                            type="text"
                            className="card-input"
                            value={card.title}
                            onChange={(event) => handleCardInputChange(card.cellNumber, 'title', event.target.value)}
                            placeholder={t('deckDetails.cards.placeholders.title')}
                            disabled={saving}
                          />
                        </td>
                        <td>
                          <textarea
                            className="card-textarea"
                            value={card.text}
                            onChange={(event) => handleCardInputChange(card.cellNumber, 'text', event.target.value)}
                            placeholder={t('deckDetails.cards.placeholders.text')}
                            rows={3}
                            disabled={saving}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="deck-footer-actions">
          <button
            type="button"
            className="ghost-button"
            onClick={() => navigate('/dashboard/my-cards')}
            disabled={saving}
          >
            {t('deckDetails.buttons.backToList')}
          </button>
          <button type="submit" className="primary-button" form="deck-form" disabled={saving}>
            {saving ? t('createDeck.buttons.creating') : (deckForm.renderMode === 'IMAGE' ? t('imageDeck.saveDraft') : t('createDeck.buttons.create'))}
          </button>
        </div>

      <CardPreviewModal
        open={previewModalOpen}
        card={{
          cellNumber: firstCard.cellNumber,
          title: firstCard.title || t('deckDetails.preview.emptyTitle'),
          text: firstCard.text || t('deckDetails.preview.emptyText'),
        }}
        onClose={() => setPreviewModalOpen(false)}
        backgroundUrl={resolvedCardBackUrl}
        textStyle={getDeckCardTextStyle(deckForm.textColor)}
        emptyMessage={t('deckDetails.preview.emptyText')}
        closeLabel={t('deckDetails.preview.close')}
      />

      {backPickerOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="modal card-back-modal">
            <div className="modal-header">
              <h3>{t('deckDetails.backPicker.title')}</h3>
              <button type="button" className="ghost-button" onClick={() => setBackPickerOpen(false)}>
                {t('deckDetails.backPicker.close')}
              </button>
            </div>
            {cardBacksLoading ? <p className="page-description">{t('deckDetails.backPicker.loading')}</p> : null}
            <div className="card-back-grid modern">
              {cardBackOptions.map((back) => (
                <div
                  key={back.id || back.imageUrl}
                  className={`card-back-card${selectedBackUrl === back.imageUrl ? ' active' : ''}`}
                  onClick={() => handleBackSelection(back.imageUrl)}
                  role="button"
                  tabIndex={0}
                >
                  {canDeleteCardBack(back) && (
                    <span className="card-back-card__actions">
                      <button
                        type="button"
                        className="card-back-card__delete"
                        onClick={(event) => {
                          event.stopPropagation();
                          requestDeleteCardBack(back);
                        }}
                        title={t('deckDetails.backPicker.deleteAction')}
                        aria-label={t('deckDetails.backPicker.deleteAction')}
                      >
                        🗑️
                      </button>
                    </span>
                  )}
                  <div
                    className="card-back-card__image"
                    style={{ backgroundImage: `url(${assetUrl(back.thumbnailUrl || back.imageUrl)})` }}
                  />
                  <span className="card-back-card__title">{back.name}</span>
                </div>
              ))}
              {deckForm.backgroundUrl &&
                !cardBackOptions.some((back) => back.imageUrl === deckForm.backgroundUrl) && (
                  <div
                    className={`card-back-card${selectedBackUrl === deckForm.backgroundUrl ? ' active' : ''}`}
                    onClick={() => handleBackSelection(deckForm.backgroundUrl)}
                    role="button"
                    tabIndex={0}
                  >
                    <div
                      className="card-back-card__image"
                      style={{ backgroundImage: `url(${assetUrl(deckForm.backgroundUrl)})` }}
                    />
                    <span className="card-back-card__title">{t('deckDetails.backPicker.current')}</span>
                  </div>
                )}
              <label className={`card-back-card add${backUploading ? ' loading' : ''}`}>
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                  onChange={handleBackFileChange}
                  ref={backFileInputRef}
                  disabled={saving || backUploading}
                />
                <span className="card-back-card__plus">+</span>
                <span className="card-back-card__title">
                  {backUploading ? t('deckDetails.backPicker.adding') : t('deckDetails.backPicker.add')}
                </span>
                <span className="card-back-card__hint">{t('deckDetails.backPicker.hint')}</span>
              </label>
            </div>
            {backUploadError && <p className="form-error">{backUploadError}</p>}
            <div className="modal-actions">
              <button type="button" className="ghost-button" onClick={() => setBackPickerOpen(false)}>
                {t('deckDetails.backPicker.cancel')}
              </button>
              <button type="button" className="primary-button" onClick={handleBackSave} disabled={saving}>
                {t('deckDetails.backPicker.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      <DeckUploadProgress value={uploadBatch} label={t('imageDeck.uploadingImages')} />
      {backDeleteConfirm && (
        <div className="modal-backdrop" role="alertdialog" aria-modal="true">
          <div className="modal">
            <div className="modal-header">
              <h3>{t('deckDetails.backPicker.deleteConfirm')}</h3>
            </div>
            {backDeleteError && <p className="form-error">{backDeleteError}</p>}
            <div className="modal-actions">
              <button type="button" className="ghost-button" onClick={closeDeleteConfirm} disabled={backDeleting}>
                {t('deckDetails.backPicker.deleteCancel')}
              </button>
              <button type="button" className="primary-button" onClick={handleBackDelete} disabled={backDeleting}>
                {backDeleting ? t('deckDetails.backPicker.deleting') : t('deckDetails.backPicker.deleteConfirmButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateDeckPage;
